// ----------------------------------------------------------------------- imports externos
import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

// ----------------------------------------------------------------------- imports internos
import { MARCA } from "../../marca";
import { SECCIONES, formatearCuenta, idActivo, puedeVer, universoDe } from "./secciones";
import { abierto, alternar, contar, guardar, leer } from "./railPlegado";
import Icono, { iconoDeUniverso } from "./Icono";
import { buscarEnMenu, destinoDe } from "./menuBuscar";

/* El rail: el mapa entero de la aplicación, agrupado por cómo se organiza el
 * trabajo.
 *
 * Tres clases de entrada, y se distinguen a la vista:
 *   · las de esta app        → link normal
 *   · las del panel FlotaBot → con una flechita, porque abren el otro sistema
 *                              en otra pestaña y conviene saberlo antes de tocar
 *   · las que faltan         → apagadas y sin tocar
 */

const Item = ({ item, cuentas, onNavegar, activo }) => {
  const total = item.cuenta ? cuentas[item.cuenta] : null;
  /* Un cero se esconde sólo cuando el número es trabajo pendiente: "Tickets 0"
   * es ruido, y peor, se lee como un aviso hasta que uno mira bien. En un
   * padrón el cero sí dice algo — que no hay ningún cliente cargado. */
  const cuenta = total === 0 && item.ocultarEnCero ? null : formatearCuenta(total);

  /* Activos e inactivos, cuando se sabe. No para todo: en tractores y furgones
   * existe el dato de si está de baja; en eventos o stock "activo" no
   * significa nada, y un número inventado es peor que ninguno.
   *
   * Va en el title y no a la vista: el rail tiene 250px y meterle tres cifras
   * por fila lo convierte en una planilla. El total alcanza para la mirada
   * rápida, y el detalle está a un hover de distancia. */
  const act = cuentas[item.cuenta + "Activos"];
  const detalle =
    total != null && act != null
      ? `${formatearCuenta(total)} en total · ${formatearCuenta(act)} activos · ${formatearCuenta(total - act)} de baja`
      : total != null
      ? `${formatearCuenta(total)} en total`
      : undefined;

  const adentro = (
    <>
      <span className="hub-punto" />
      <span className="txt">{item.label}</span>
      {cuenta && (
        <span className="hub-n" title={detalle}>
          {cuenta}
        </span>
      )}
    </>
  );

  if (item.donde === "pronto") {
    return (
      <span className="hub-item pronto" title="Todavía no está hecho">
        <span className="hub-punto" />
        <span className="txt">{item.label}</span>
        {cuenta && <span className="hub-n">{cuenta}</span>}
      </span>
    );
  }

  // Las de FlotaBot también son links internos: se muestran acá adentro, en
  // /panel/<id>, sin sacar a nadie de la ventana.
  const destino = destinoDe(item);

  /* `activo` viene calculado de afuera cuando hay un ganador: react-router
     compara sólo el path, y en `/rto?vista=agenda` marcaba la agenda Y el RTO
     al mismo tiempo. Cuando no hay ganador —una ruta que el menú no conoce—
     se cae al `isActive` de siempre. */
  return (
    <NavLink
      to={destino}
      onClick={onNavegar}
      className={({ isActive }) =>
        "hub-item" +
        ((activo === null || activo === undefined ? isActive : activo)
          ? " activo"
          : "")
      }
    >
      {adentro}
    </NavLink>
  );
};

const Rail = ({ plegado, onPlegar, onNavegar, cuentas = {}, permisos = null }) => {
  /* Qué sectores están plegados. Arranca de `localStorage` en el primer render
   * y no en un efecto: leído después, el rail se dibujaría largo y se plegaría
   * solo medio segundo más tarde, que se ve como un error de la pantalla. */
  const [plegados, setPlegados] = useState(() =>
    leer(typeof window !== "undefined" ? window.localStorage : null),
  );
  const { pathname, search } = useLocation();
  const universoActivo = universoDe(pathname, undefined, search);
  // Cuál es EL ítem abierto. Mismo cálculo que el título, así que el rail y
  // el encabezado no pueden contradecirse. Se llama `idAbierto` y no `abierto`
  // porque `abierto` ya es la función de los sectores plegados, importada
  // arriba — pisarla dejaba el rail sin dibujar.
  const idAbierto = idActivo(pathname, search);

  useEffect(() => {
    guardar(plegados, typeof window !== "undefined" ? window.localStorage : null);
  }, [plegados]);

  const alternarUniverso = (universo) =>
    setPlegados((p) => alternar(p, universo));

  /* Tooltip propio para el rail colapsado. El `title` nativo es lento y, sobre
   * todo, un tooltip a la derecha lo recortaría el `overflow` del rail; éste va
   * `position: fixed` (fuera del <nav>) y aparece al instante. */
  const [tip, setTip] = useState(null); // { nombre, x, y } | null

  /* Buscar una pantalla en el menú (ticket 384). Mientras hay algo escrito,
   * el menú deja de ser el mapa y pasa a ser la lista de lo que coincide. */
  const [buscado, setBuscado] = useState("");
  const navegar = useNavigate();

  /* Colapsado, tocar un ícono no puede dejarte donde estabas: reabre el rail,
   * ABRE ese universo (lo saca de los plegados) y lo trae a la vista. Antes sólo
   * reabría, y si estabas en Tráfico y tocabas Mi espacio, seguías en Tráfico. */
  const abrirEnUniverso = (universo) => {
    setPlegados((p) => {
      const s = new Set(p);
      s.delete(universo);
      return s;
    });
    setTip(null);
    onPlegar(); // colapsado → abierto (onPlegar alterna)
    if (typeof window !== "undefined") {
      window.setTimeout(() => {
        const el = document.querySelector(`.hub-universo-btn[data-universo="${universo}"]`);
        if (el && el.scrollIntoView) el.scrollIntoView({ block: "center" });
      }, 40);
    }
  };

  /* Se muestra lo que la persona puede abrir. Mientras los permisos no
   * llegaron (null) se muestra todo: parpadear con el menú a medio armar es
   * peor que mostrar de más por medio segundo, y de todas formas lo que
   * protege es la base, no esta lista. */
  const puede = (item) => puedeVer(item, permisos);

  /* `oculto` es una sección del catálogo que NO va en el rail. La usa el RTO:
   * dejó de ser un ítem del menú —ahora es una columna del padrón— pero sigue
   * en el catálogo para darles permiso y título a los avisos de la home y al
   * enlace de la ficha que abren `/rto`. Sacarla del catálogo apagaría esas
   * tarjetas; sacarla sólo de acá es justo lo que se pidió. */
  const grupos = SECCIONES.map((g) => ({
    ...g,
    items: g.items.filter((i) => !i.oculto && puede(i)),
  })).filter((g) => g.items.length > 0);

  /* El encabezado de universo se dibuja sólo cuando CAMBIA, recorriendo la
   * lista en orden. Calcularlo agrupando de nuevo por universo daría lo mismo
   * hoy y se rompería el día que alguien intercale un grupo — y se rompería
   * silenciosamente, repitiendo el título en dos lugares.
   *
   * Y el subtítulo se omite cuando dice lo mismo que el universo (Portería
   * dentro de Portería): dos renglones iguales pegados se leen como un error
   * de la pantalla, no como una jerarquía. */
  let universoPrevio = null;

  const encontradas = buscarEnMenu(grupos, buscado);
  const buscando = !plegado && buscado.trim() !== "";
  const alElegir = () => {
    setBuscado("");
    if (onNavegar) onNavegar();
  };
  const teclaEnBuscar = (e) => {
    if (e.key === "Escape") {
      setBuscado("");
    } else if (e.key === "Enter" && encontradas.length > 0) {
      e.preventDefault();
      navegar(destinoDe(encontradas[0].item));
      alElegir();
    }
  };

  return (
  <>
  <nav className="hub-rail" aria-label="Secciones">
    <div className="hub-marca">
      {/* Sin escudo. Estaba el camión azul heredado de CantApp, que ya no es la
          marca; y un cuadradito de color vacío en su lugar es peor que nada,
          porque parece una imagen que no cargó. El nombre solo alcanza. */}
      {/* Va la marca sola, sin bajada. En 250px de ancho, "FiBOT · Gestión de
          Flotas" se corta o se achica hasta no leerse — y quien está mirando el
          rail ya sabe perfectamente dónde está. */}
      <b>{MARCA}</b>
      <button
        className="hub-plegar"
        onClick={onPlegar}
        aria-label={plegado ? "Abrir menú" : "Plegar menú"}
        title={plegado ? "Abrir menú" : "Plegar menú"}
      >
        {plegado ? "›" : "‹"}
      </button>
    </div>

    {/* «Inicio» YA NO SE DIBUJA ACÁ A MANO. Era un enlace fijo a `/` porque
        `/` era el saludo con la hora, que no estaba en el menú. Ahora `/` es
        Novedades y sale de `SECCIONES` como cualquier otra entrada — con los
        dos, el rail mostraba «Inicio» dos veces y había que acordarse de
        cambiar el rótulo en dos lugares. */}
    {!plegado && (
      <div className="hub-menu-buscar">
        <input
          type="search"
          value={buscado}
          onChange={(e) => setBuscado(e.target.value)}
          onKeyDown={teclaEnBuscar}
          placeholder="Buscar en el menú"
          aria-label="Buscar en el menú"
          autoComplete="off"
          spellCheck={false}
        />
      </div>
    )}
    {buscando && (
      <div className="hub-menu-encontradas" role="group" aria-label="Pantallas encontradas">
        {encontradas.length === 0 && <div className="hub-grupo">Nada con ese nombre</div>}
        {encontradas.map(({ item, universo, grupo }, i) => {
          const antes = encontradas[i - 1];
          const lugar = grupo && grupo !== universo ? `${universo} · ${grupo}` : universo;
          const lugarAntes = antes
            && (antes.grupo && antes.grupo !== antes.universo
              ? `${antes.universo} · ${antes.grupo}` : antes.universo);
          return (
            <div key={item.id}>
              {lugar !== lugarAntes && <div className="hub-grupo">{lugar}</div>}
              <Item item={item} cuentas={cuentas} onNavegar={alElegir}
                    activo={idAbierto ? item.id === idAbierto : null} />
            </div>
          );
        })}
      </div>
    )}
    {!buscando && grupos.map(({ universo, grupo, items }) => {
      const abreUniverso = universo && universo !== universoPrevio;
      universoPrevio = universo || universoPrevio;
      const seVe = abierto(universo, plegados, universoActivo);
      /* El título del universo se dibuja cuando CAMBIA; los ítems, sólo si
         está abierto. Los grupos siguientes del mismo universo se saltean
         enteros —sin el título, que ya se dibujó— o el sector plegado
         mostraría sus subtítulos sueltos, sin nada abajo. */
      return (
        <div key={universo + "/" + grupo}>
          {abreUniverso && (
            <button
              type="button"
              className={"hub-universo hub-universo-btn" + (seVe ? "" : " plegado")
                + (universo === universoActivo ? " activo" : "")}
              data-universo={universo}
              /* Colapsado, el click reabre el rail Y abre este sector (te lleva
                 ahí); abierto, pliega/despliega el universo como siempre. */
              onClick={() => (plegado ? abrirEnUniverso(universo) : alternarUniverso(universo))}
              /* Tooltip propio sólo cuando está colapsado (íconos sueltos). */
              onMouseEnter={
                plegado
                  ? (e) => {
                      const r = e.currentTarget.getBoundingClientRect();
                      setTip({ nombre: universo, x: r.right, y: r.top + r.height / 2 });
                    }
                  : undefined
              }
              onMouseLeave={plegado ? () => setTip(null) : undefined}
              aria-expanded={seVe}
              /* El activo no se puede plegar: es lo único que permite que el
                 menú marque dónde estás parado. Se dice, en vez de que el
                 click no haga nada sin explicación. Pero colapsado SÍ se puede
                 tocar (abre el rail). */
              disabled={!plegado && universo === universoActivo}
              /* El nombre accesible dice la ACCIÓN y no sólo el sector: el
                 contenido visible es «Tráfico ▾», y leído en voz alta eso no
                 dice que se pueda plegar ni en qué estado está. */
              aria-label={
                plegado
                  ? `${universo} — abrir menú`
                  : universo === universoActivo
                    ? `${universo} — es el sector de la pantalla abierta`
                    : seVe
                      ? `Plegar ${universo}`
                      : `Abrir ${universo}`
              }
              /* Abierto: el title dice la acción (plegar/abrir el sector).
                 Colapsado NO lleva title: el nombre lo muestra el tooltip propio
                 (`.hub-tip`), que aparece al instante y no lo recorta el rail. */
              title={
                plegado
                  ? undefined
                  : universo === universoActivo
                    ? "Es el sector de la pantalla abierta"
                    : seVe
                      ? `Plegar ${universo}`
                      : `Abrir ${universo}`
              }
            >
              <Icono nombre={iconoDeUniverso(universo)} className="hub-universo-ico" />
              <span className="txt">{universo}</span>
              {/* SIN EL NÚMERO, un universo plegado y uno vacío se ven igual —
                  y el que no sabe que Tráfico tiene nueve pantallas no las va
                  a buscar. */}
              {!seVe && (
                <span className="hub-universo-n">{contar(grupos, universo)}</span>
              )}
              <span className="hub-universo-flecha" aria-hidden="true">
                {seVe ? "▾" : "▸"}
              </span>
            </button>
          )}
          {seVe && grupo !== universo && (
            <div className="hub-grupo">{grupo}</div>
          )}
          {seVe &&
            items.map((item) => (
              <Item
                key={item.id}
                item={item}
                cuentas={cuentas}
                onNavegar={onNavegar}
                activo={idAbierto ? item.id === idAbierto : null}
              />
            ))}
        </div>
      );
    })}
  </nav>
  {plegado && tip && (
    <div className="hub-tip" role="tooltip" style={{ top: tip.y, left: tip.x + 8 }}>
      {tip.nombre}
    </div>
  )}
  </>
  );
};

export default Rail;
